import { z } from "zod";
import { ReferralPct, markupUnitPrice, unitPriceFromYen } from "./referral.js";
import { VendorQuoteStatus } from "./vendorQuote.js";
import { VendorQuoteListState } from "./vendorQuoteList.js";

// ---------------------------------------------------------------------------
// 外注見積から見積を作る（#84、docs/34、DB 050）
//   見積の行の「取込」→ 外注見積の行を選んで（1行／まとめ／分ける／行にして追加）粗利率で自社の行にする。
//   金額の計算は DB（fn_estimate_import_vendor_lines・fn_recalc_all）。画面はその場の見せ方（予定の売価・分け方）だけ、
//   ここの純粋な関数（付録B）で同じ式を使う。
// ---------------------------------------------------------------------------

const Yen = z.number().int().min(-999_999_999_999).max(999_999_999_999);
const Uuid = z.string().uuid();

/** 入れ方（docs/34 決めごと4・7）：into＝この行に入れる（1→1）／merge＝まとめてこの行に入れる（N→1）／append＝行にして追加（N→N）／split＝分けて入れる（1→N） */
export const VendorImportMode = z.enum(["into", "merge", "append", "split"]);
export type VendorImportMode = z.infer<typeof VendorImportMode>;
/**
 * 画面から送る入れ方：DB の4つ ＋ intoBelow＝この行と下に入れる（#141、docs/34 §9.2）。
 * intoBelow は DB の関数に渡さない：API が intoBelowPlan で into 1回と append に分け、同じトランザクションで続けて呼ぶ（cost_mappings の import_mode は into／append のまま）
 */
export const VendorImportRequestMode = z.enum(["into", "merge", "append", "split", "intoBelow"]);
export type VendorImportRequestMode = z.infer<typeof VendorImportRequestMode>;

/**
 * 粗利の決め方（決めごと5）：margin＝粗利率％（売価に対する。0〜99.99・小数2桁）／yen＝行ごとに上乗せする円。
 * referralPct（#213、docs/40 §5.4）：パートナー ON の見積の「＋パートナー［ ］％」。単価＝仕入れの単価 ÷ (1 − 粗利率 − パートナー)。
 * 行のパートナー欄は見積の率と同じなら空（見積の率に従う）、違えばその率（DB が決める）。粗利率との合計は 100% 未満
 */
export const VendorImportPricing = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("margin"), value: z.number().min(0).max(99.99).multipleOf(0.01), referralPct: ReferralPct.optional() }),
  z.object({ kind: z.literal("yen"), value: Yen.min(-999_999_999).max(999_999_999) }),
]);
export type VendorImportPricing = z.infer<typeof VendorImportPricing>;

export const SplitMode = z.enum(["sale", "qty", "manual"]);
export type SplitMode = z.infer<typeof SplitMode>;
export const SPLIT_MODE_LABEL: Record<SplitMode, string> = { sale: "売価の割合", qty: "数量", manual: "手で" };

/** POST /estimates/:id/vendor-import */
export const VendorImportRequest = z.object({
  vendorQuoteId: Uuid,
  mode: VendorImportRequestMode,
  /** into・merge・intoBelow：入れる先の行 */
  targetLineId: Uuid.nullable().default(null),
  /** append：この行の下に追加。null＝紙の最後 */
  afterLineId: Uuid.nullable().default(null),
  /** append：内訳の紙があるとき、どの紙に追加するか（afterLineId があればその行の紙） */
  sheetId: Uuid.nullable().default(null),
  /** 外注の行の id（into・split は1つ、merge は2つ以上。intoBelow は選んだ順＝1つ目がこの行、残りがこの行の下に並ぶ順） */
  lineIds: z.array(Uuid).min(1).max(500),
  pricing: VendorImportPricing,
  /** split：分ける先の行と仕入れの分け（和が外注の金額でなければ画面に「残り」） */
  shares: z.array(z.object({ lineId: Uuid, amount: Yen.min(0) })).max(50).default([]),
  splitMode: SplitMode.default("sale"),
});
export type VendorImportRequest = z.infer<typeof VendorImportRequest>;

/** POST /estimates/:id/vendor-unlink：「外す」＝仕入れを手入力に戻す（値は残す） */
export const VendorUnlinkRequest = z.object({ lineId: Uuid });
export type VendorUnlinkRequest = z.infer<typeof VendorUnlinkRequest>;

/** 取り込んだ外注の行 → 見積のどの行に入ったか（取込の画面の「取込済み → 5 LANケーブル」） */
export const VendorImportedLine = z.object({
  vendorLineId: Uuid,
  lineId: Uuid,
  lineageId: Uuid,
  lineNo: z.number().int(),
  name: z.string(),
  /** その行の内訳の名前（#282。表紙・1枚の見積は null）。取込の1枚の「取込済 → 行 3 銅材（B案）」 */
  sheetName: z.string().nullable().default(null),
  mode: VendorImportMode,
  /** その行に入れた仕入れ（分けたときはその行の分） */
  amount: Yen,
});
export type VendorImportedLine = z.infer<typeof VendorImportedLine>;

/** 外注見積ごとの進捗（帯と取込の画面の「全 n行のうち 取込済み／残り」。v_vendor_quote_progress） */
export const VendorQuoteProgress = z.object({
  vendorQuoteId: Uuid,
  status: VendorQuoteStatus,
  /** 一覧と同じ状態（#274、docs/99 §4＝v_vendor_quote_list.state。帯の「状態」の列は一覧と同じ言葉＝vendorQuoteStateLabel）。ビューに無ければ null */
  state: VendorQuoteListState.nullable().default(null),
  /** 反映済みのうち一部だけ（「一部反映」） */
  partial: z.boolean().default(false),
  vendorName: z.string(),
  quoteNo: z.string(),
  quotedOn: z.string().nullable(),
  sourceLabel: z.string(),
  pageCount: z.number().int().nullable(),
  totalExTax: Yen.nullable(),
  taxIncluded: z.boolean(),
  taxRate: z.number(),
  /** 取り込める行（明細・表紙の行・別途・金額なし）の数と税抜の和 */
  allCount: z.number().int(),
  allAmount: Yen,
  doneCount: z.number().int(),
  doneAmount: Yen,
  restCount: z.number().int(),
  restAmount: Yen,
  /** 取り込んだ行の売価の和と仕入れの和（下回りの判定。決めごと9） */
  saleAmount: Yen,
  costAmount: Yen,
  ownLineCount: z.number().int(),
  imported: z.array(VendorImportedLine),
});
export type VendorQuoteProgress = z.infer<typeof VendorQuoteProgress>;

/** GET /estimates/:id/vendor-progress */
export const VendorProgress = z.object({
  /** 会社の設定「外注見積の粗利率の既定」（tenants.vendor_margin_pct） */
  marginPct: z.number(),
  /** 上乗せ後の単価の丸め（#213。tenants.markup_unit_price_round。1／10／100円・切り上げ） */
  round: z.number().int().default(1),
  /** 同じ案件の外注見積（読み取りが終わったものも、まだのものも。lines を選べるのは status が review／done／manual のとき） */
  quotes: z.array(VendorQuoteProgress),
});
export type VendorProgress = z.infer<typeof VendorProgress>;

/** 取込の結果（Estimate に添える） */
export const VendorImportResult = z.object({
  lines: z.number().int(),
  cost: Yen,
  lineIds: z.array(Uuid),
  skipped: z.number().int(),
  message: z.string(),
});
export type VendorImportResult = z.infer<typeof VendorImportResult>;

export const VENDOR_IMPORT_MSG = {
  // 通知（#283、docs/99 §14：「行 n に取り込みました」の形。前は「「名前」を n の行に入れました」）
  into: "行 {no} に取り込みました（仕入れ {cost}）",
  merge: "{n}行を合算して行 {no} に取り込みました（仕入れ {cost}）。名前を入力してください",
  intoBelow: "{n}行を行 {no} とその下に取り込みました（仕入れ {cost}）",
  intoBelowSkipped: "{n}行を行 {no} とその下に取り込みました（仕入れ {cost}）。値引きの行 {skipped}行は取り込んでいません（合算のときだけ入ります）",
  /** #141：入れる先の行に名称があり、写すと変わるとき（止めない） */
  replaceName: "この行の{lineName}を置き換えます",
  negativeIntoBelow: "値引きの行は「この行と下に取込」「新規行に追加」では入りません",
  append: "{n}行を追加しました（仕入れ {cost}）",
  appendSkipped: "{n}行を追加しました（仕入れ {cost}）。値引きの行 {skipped}行は取り込んでいません（合算のときだけ入ります）",
  split: "「{name} {amount}」を {n}行に分割して取り込みました",
  unlinked: "連携を解除しました（仕入れは手入力として残ります）",
  /** #282：断り。{no}＝入っている見積の行の番号、{sheet}＝内訳の名前（あれば「（B案）」）。DB の HINT も同じ形 */
  used: "見積の行 {no}{sheet} に取込済（行の「{vendorQuote}連携解除」で戻せます）",
  negative: "値引きの行は単独では取り込めません（合算のときだけ合計に入ります）",
  /** 押すと値引きが黙って抜ける注意（#92 決め9：残して1文に。合算の取込方法は取込の「？」） */
  negativeAppend: "値引きの行は「新規行に追加」では入りません",
  role: "小計・合計・見出しの行は取り込めません",
  notReady: "AI読み取りが終わってから取り込めます",
  groupMismatch: "同じ{project}の外注見積から取り込みます",
  issuedAppend: "出したあとの見積には行を足せません（「訂正版作成」か、仕入れだけ入れます）",
  lineNotFound: "入れる先の行が見つかりません（保存してからもう一度）",
  sheetRequired: "どの紙に追加するかを選んでください",
  nameRequired: "名前を入れてください（まとめた行）",
  /** #274（docs/99 §4、★3）：帯・取込の1枚の札。文（〜ました）にしない */
  allDone: "全行取込済",
  none: "外注見積が登録されていません。{project}の「外注見積」から登録すると、ここから行を選べます",
  reading: "AI読み取りが終わると、行を選べます",
  underLine: "原価割れ",
  /** #274（docs/99 §4、★4）：帯の売価の列の札。朱の札は「原価割れ」で始める（色だけで伝えない）。粗利の札は藍 */
  underBand: "原価割れ（売価 {sale} ＜ 仕入れ {cost}）",
  underTotal: "原価割れ {diff}円（売価 {sale} ＜ 外注見積 {total}）",
  /** 全行取込済で売価の合計が外注見積の税抜合計以上：粗利＝売価の合計 − 外注見積の税抜合計、％＝粗利 ÷ 売価の合計（小数1桁。取込の1枚の粗利率と同じ見方） */
  overTotal: "粗利 {diff}円（{pct}%）",
  helpMargin: "粗利率＝売価に対する％、上乗せ率＝仕入れに対する％。どちらを打ってももう一方が変わります（20% ⇄ 25%）。単価は 仕入れの単価 ÷ (1 − 粗利率) を設定の単位（{round}円）で切り上げ。",
  /** #213：パートナー ON の見積 */
  helpMarginRef: "粗利率＝売価に対する％、上乗せ率＝仕入れに対する％。単価は 仕入れの単価 ÷ (1 − 粗利率 − {referrer}) を設定の単位（{round}円）で切り上げ（20%＋10% ＝ 42.86%）。",
  helpYen: "「円」は 金額＝外注の金額＋円 → 単価＝金額÷数量 を設定の単位（{round}円）で切り上げ。金額は数量×単価なので 1 円単位でずれることがあります。",
} as const;

/**
 * 取込の1枚の言葉（#283、docs/99 §14・提案の表 ⑨）。ボタンは「を」「の」を入れない（#238）・説明の文は「？」（取込方法）へ。
 * {vendorQuote}・{lineName} などは画面が t() で埋める
 */
export const VENDOR_PANEL_MSG = {
  title: "{vendorQuote}から取込",
  /** 取込先：見積の行 3・4／新しい行 */
  target: "取込先",
  targetLines: "見積の行 {nos}",
  targetNew: "新しい行",
  addSplit: "＋ 分割行追加",
  /** 進み：全 14行｜取込済 2行｜未取込 12行 84,539円 */
  all: "全 {n}行",
  done: "取込済 {n}行",
  rest: "未取込 {n}行 {amount}円",
  /** 選んだ行：選択 3行 22,748円（→ 売価 28,435円） */
  selected: "選択 {n}行 {amount}円",
  toSale: "→ 売価 {amount}円",
  loading: "読込中…",
  byRate: "率",
  byYen: "円",
  taxIncluded: "税込→税抜換算（{rate}%）",
  issued: "発行済の回：仕入れのみ反映",
  cand: "候補＝名前が似た行",
  howTitle: "取込方法",
  into: "この行に取込",
  merge: "合算してこの行に取込",
  split: "分割して取込",
  append: "新規行に追加",
  /** 分割の枠の見出し：「機器回り設置取り回し 17,700」を 2行に分割 */
  splitHead: "「{name} {amount}」を {n}行に分割",
  negOnlyMerge: "値引き（合算のときだけ）",
  /** #282：外注の行の状態「取込済 → 行 3 銅材（B案）」と、その行の連携を外す「解除」 */
  usedTo: "取込済 → 行 {no} {name}",
  unlink: "解除",
} as const;

/** 見積書の表の No.（明細・率・別途・値引きの行だけを上から数える＝画面の EstimateTable の NUMBERED と同じ）。数えない行・無い行は null */
export const NUMBERED_ROLES: ReadonlySet<string> = new Set(["detail", "overhead_rate", "excluded", "discount"]);
export function displayLineNo(lines: readonly { id: string; role: string }[], id: string): number | null {
  let n = 0;
  for (const l of lines) {
    if (NUMBERED_ROLES.has(l.role)) n++;
    if (l.id === id) return NUMBERED_ROLES.has(l.role) ? n : null;
  }
  return null;
}

/** 断りの文（VENDOR_IMPORT_MSG.used）：内訳の名前があれば「（B案）」を足す */
export function vendorUsedText(no: number | string, sheet: string | null | undefined): string {
  return VENDOR_IMPORT_MSG.used.replace("{no}", String(no)).replace("{sheet}", sheet ? `（${sheet}）` : "");
}

/**
 * 帯の売価の列の札（#274、docs/99 §4）。外注見積ごとに：
 *   ・取り込んだ行の売価 ＜ 仕入れ → 朱「原価割れ（売価 40,000 ＜ 仕入れ 44,000）」
 *   ・全行取込済で 売価の合計 ＜ 外注見積の税抜合計 → 朱「原価割れ −3,000円（売価 85,000 ＜ 外注見積 88,000）」
 *   ・全行取込済で 売価の合計 ≧ 外注見積の税抜合計 → 藍「粗利 22,000円（20.0%）」（％＝粗利 ÷ 売価の合計、小数1桁。売価が 0 なら ％ は 0.0）
 *   ・それ以外（一部だけ取込で原価割れでない）→ 札なし
 * yen は 3桁区切りの関数（画面の lib/num の yen と同じ形）を渡す
 */
export type VendorSaleChip = { tone: "shu" | "ai"; text: string } | null;
export function vendorSaleChip(q: Pick<VendorQuoteProgress, "restCount" | "allCount" | "doneCount" | "saleAmount" | "costAmount" | "totalExTax" | "allAmount">, yen: (n: number) => string): VendorSaleChip {
  const allDone = q.restCount === 0 && q.allCount > 0;
  if (q.doneCount > 0 && q.saleAmount < q.costAmount) {
    return { tone: "shu", text: VENDOR_IMPORT_MSG.underBand.replace("{sale}", yen(q.saleAmount)).replace("{cost}", yen(q.costAmount)) };
  }
  if (!allDone) return null;
  const total = q.totalExTax ?? q.allAmount;
  const diff = q.saleAmount - total;
  if (diff < 0) {
    return { tone: "shu", text: VENDOR_IMPORT_MSG.underTotal.replace("{diff}", `−${yen(-diff)}`).replace("{sale}", yen(q.saleAmount)).replace("{total}", yen(total)) };
  }
  const pct = q.saleAmount > 0 ? (Math.round((diff / q.saleAmount) * 1000) / 10).toFixed(1) : "0.0";
  return { tone: "ai", text: VENDOR_IMPORT_MSG.overTotal.replace("{diff}", yen(diff)).replace("{pct}", pct) };
}

// ---------------------------------------------------------------------------
// 付録B の計算（画面の予定の見せ方。確定の値は DB が計算する）
// ---------------------------------------------------------------------------

/** 粗利率 m（売価に対する％）→ 上乗せ率 p（仕入れに対する％）。m が 100 以上なら null */
export function markupOfMargin(m: number): number | null {
  if (!Number.isFinite(m) || m >= 100) return null;
  return (m / (100 - m)) * 100;
}
/** 上乗せ率 p → 粗利率 m */
export function marginOfMarkup(p: number): number {
  return (p / (100 + p)) * 100;
}
/** 小数1桁に */
export function pct1(v: number | null): number | null {
  return v == null ? null : Math.round(v * 10) / 10;
}
/**
 * 率で：単価（売価）＝ 仕入れの単価 ÷ (1 − 粗利率 − パートナー) を round 円で切り上げ（#213。前は1円の四捨五入。DB の fn_markup_unit_price と同じ）。
 * 粗利率＋パートナーが 100 以上なら 0
 */
export function pricedUnit(costUnit: number, marginPct: number, referralPct: number = 0, round: number = 1): number {
  return markupUnitPrice(costUnit, marginPct + referralPct, round) ?? 0;
}
/** 税込の外注見積は税抜に（四捨五入。DB の fn_vendor_line_ex_tax と同じ） */
export function exTax(amount: number | null, taxIncluded: boolean, taxRate: number): number {
  if (amount == null) return 0;
  return taxIncluded ? Math.round(amount / (1 + taxRate / 100)) : amount;
}
/** 外注の1行を入れたときの 数量・単価 の予定（DB と同じ式。round＝上乗せ後の単価の丸め） */
export function plannedLine(
  l: { quantity: number | null; unitPrice: number | null; amount: number | null },
  q: { taxIncluded: boolean; taxRate: number },
  pricing: VendorImportPricing,
  round: number = 1,
): { quantity: number; unitPrice: number; cost: number } {
  const cost = exTax(l.amount, q.taxIncluded, q.taxRate);
  const qty = l.quantity != null && l.quantity > 0 ? l.quantity : 1;
  const costUnit = l.unitPrice != null ? exTax(l.unitPrice, q.taxIncluded, q.taxRate) : Math.round(cost / qty);
  const unitPrice = pricing.kind === "yen" ? (unitPriceFromYen(cost, qty, pricing.value, round) ?? 0) : pricedUnit(costUnit, pricing.value, pricing.referralPct ?? 0, round);
  return { quantity: qty, unitPrice, cost };
}
/** 分ける：外注の金額を 売価の割合／数量／手で に分ける。端数は最後の行。manual は打った値のまま（残りは呼ぶ側が出す） */
export function splitShares(
  total: number,
  targets: { amount: number; quantity: number | null; manual: number }[],
  mode: SplitMode,
): number[] {
  if (mode === "manual") return targets.map((t) => Math.max(0, Math.round(t.manual || 0)));
  const base = targets.map((t) => (mode === "qty" ? Math.max(0, t.quantity ?? 0) : Math.max(0, t.amount)));
  const sum = base.reduce((a, b) => a + b, 0);
  const out = base.map((b) => (sum > 0 ? Math.round((total * b) / sum) : 0));
  if (out.length) out[out.length - 1] = total - out.slice(0, -1).reduce((a, b) => a + b, 0);
  return out;
}

/**
 * この行と下に入れる（#141）の分け方。ids は選んだ順。
 *   値引き（金額が負）の行は飛ばす（「行にして追加」と同じ。数は skipped）。
 *   first＝値引きでない1つ目（この行に into）、runs＝残りを append で足す塊。
 *   DB の append は塊の中を外注の行の順（line_no）に並べるので、選んだ順のまま並ぶよう line_no が増えている所で塊を切る
 *   （前の塊の最後の行の下に次の塊を足す）。見つからない id は値引きでないとして渡す（DB が VENDOR_LINE_NOT_FOUND で断る）
 */
export function intoBelowPlan(ids: readonly string[], lines: readonly { id: string; amount: number | null; lineNo: number }[]): { first: string | null; runs: string[][]; skipped: number } {
  const by = new Map(lines.map((l) => [l.id, l]));
  const seen = new Set<string>();
  const pos: string[] = [];
  let skipped = 0;
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const a = by.get(id)?.amount;
    if (a != null && a < 0) skipped++;
    else pos.push(id);
  }
  const runs: string[][] = [];
  let run: string[] = [];
  for (const id of pos.slice(1)) {
    const prev = run[run.length - 1];
    if (prev !== undefined && (by.get(id)?.lineNo ?? 0) <= (by.get(prev)?.lineNo ?? 0)) {
      runs.push(run);
      run = [];
    }
    run.push(id);
  }
  if (run.length) runs.push(run);
  return { first: pos[0] ?? null, runs, skipped };
}

/** 名前の正規化（候補の判定。記号・空白・「工事」「一式」「同上」などを除き、ひらがな→カタカナ、小文字） */
export function normName(s: string | null | undefined): string {
  let t = (s ?? "").normalize("NFKC").toLowerCase();
  t = t.replace(/[\s　・･,、。．.\-‐－ー—―~〜～()（）[\]［］{}「」『』:：;；'"’”`/／\\|＊*＋+=＝!！?？#＃&＆%％@＠]/g, "");
  t = t.replace(/(工事|一式|同上|各|等|ほか|他|材料|部材|費)/g, "");
  return t.replace(/[ぁ-ゖ]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60));
}
/** 候補（決めごと13）：見積の行の名前と、外注の行の名前か規格が 同じ・含む */
export function isCandidate(ownName: string, l: { name: string; spec: string }): boolean {
  const a = normName(ownName);
  if (!a) return false;
  const b = normName(l.name);
  const c = normName(l.spec);
  return (!!b && (a === b || a.includes(b) || b.includes(a))) || (!!c && (a === c || c.includes(a)));
}
